import React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from '../components/Layout';
import './JobDetailPage.css';

const JobDetailPage = () => {
  const { jobSlug } = useParams();
  const navigate = useNavigate();

  // Slug to ID mapping
  const slugToId = {
    "RA-electronics-design-engineer": 1,
    "SRF-electronics-design-engineer": 2,
    "JRF-electronics-design-engineer": 3,
    "RA-ML-AI-engineer-livestock-AI": 4,
    "SRF-ML-AI-engineer-livestock-AI": 5,
    "JRF-ML-AI-engineer-livestock-AI": 6,
    "RA-microbiologist-livestock-AI": 7,
    "SRF-microbiologist-livestock-AI": 8,
    "JRF-biotechnologist-livestock-AI": 9,
    "project-assistant-microbiology": 10,
    "project-assistant-electronics": 11,
    "project-assistant-ai": 12
  };

  const jobId = slugToId[jobSlug];

  // Job details data
  const jobDetails = {
    1: {
      title: "Research Associate I - Electronics Design Engineer",
      duration: "Up to 36 months (may extend)",
      position: "Research Associate I – Electronics Design Engineer",
      fellowship: "As per current norms",
      location: "Coimbatore, Tamil Nadu",
      type: "Full-time Research Position",
      roleSummary: "We seek for a Research Associate I ( Electronics ), who will lead the design, development, and validation of a wearable, non-invasive system for diseases treatment. The role requires independent technical ownership of electronics architecture and close collaboration with AI/ML and microbiology teams.",
      responsibilities: [
        "Architect and implement the end-to-end electronics system for a wearable device, including signal generation, sensing, power management, and embedded control.",
        "Design, simulate, and validate circuitry capable of producing precisely controlled signal.",
        "Lead schematic design, PCB layout ( multilayer, layer, mixed signal ), fabrication oversight, and bring up for up for low, noise, low power biomedical electronics.",
        "Engineer current controlled electrode drive circuits with safety compliant isolation, impedance monitoring, and fault detection suitable for livestock application.",
        "Develop and integrate embedded firmware ( MCU based ) for real-time signal control, sensor acquisition, and communication with the AI control layer.",
        "Implement closed loop hardware interfaces enabling AI driven adjustment of stimulation parameters based on infection severity and biological feedback.",
        "Conduct bench level validation, environmental testing, and level validation, environmental testing, and field readiness testing ( temperature, humidity, motion artefacts ).",
        "Generate technical documentation for design verification, certification readiness ( BIS / ISO ) and patent filings.",
        "Provide technical mentorship to JRFs and coordinate electronics deliverables across clinical and field trial phases."
      ],
      qualifications: [
        "PhD ( submitted / awarded ) in Electronics / Electrical / Instrumentation / Biomedical Engineering.",
        "Minimum 3 to 4 years of research experience in R&D projects.",
        "Wearable or biomedical device development.",
        "Signal processing or electromagnetic stimulation systems.",
        "Power efficient embedded system design.",
        "Regulatory exposure ( BIS / ISO / IEC – desirable )."
      ]
    },
    2: {
      title: "Senior Research Fellow ( SRF ) – Electronics Design Engineer",
      duration: "Up to 36 months (may extend)",
      position: "Senior Research Fellow (SRF) – Electronics Design Engineer",
      fellowship: "As per current norms",
      location: "Coimbatore, Tamil Nadu",
      type: "Full-time Research Position",
      roleSummary: "We seek for an experienced Senior Research Fellow ( Electronics Design Engineer ) to lead research activities in advanced electronics circuit design, power management architecture, sensor systems, and circuit analysis. This role includes project leadership, system level design, mentoring juniors, and managing technical deliverables.",
      responsibilities: [
        "Lead development of complex electronic circuits and subsystems.",
        "Perform advanced circuit analysis, including transient modeling, noise optimisation, power integrity, and signal integrity.",
        "Architect and optimize power management systems for low-power electronics.",
        "Integrate multisensor platforms with calibration and conditioning circuits.",
        "Conduct simulation, modeling, PCB design, validation, and optimization.",
        "Guide JRFs / interns and prepare research documentation and publications. Strong expertise in electronic circuit design and analysis.",
        "Solid understanding of power electronics and low-power architectures.",
        "Hands-on experience with sensor systems and embedded hardware development.",
        "Ability to lead projects, mentor juniors, and deliver technical outputs."
      ],
      qualifications: [
        "Must meet all JRF qualifications: M.E. / M.Tech or PhD ( pursuing / completed ) in Electronics, Electrical, ECE, Instrumentation, Embedded Systems, etc.",
        "Minimum 2 to 3 years of research experience in R&D projects.",
      ],
      desiredSkills: [
        "Qualify through one of the following national level exams:",
        "CSIR–UGC NET",
        "GATE",
        "Equivalent exams by central government agencies such as DST, DBT, DAE, DOS, DRDO, MHRD, ICAR, ICMR, IIT, IISc, IISER, etc.",
        "( *- In case candidate has not qualified for the exams above or meet the essential criteria, then they will qualify only as Project associate )",
      ],
     
      engineeringSoftwareTools: [
        "Circuit simulation: LTspice, PSpice, ADS ( nonRF functions ), MATLAB Simulink.",
        "PCB design: Altium, OrCAD, KiCad.",
        "Embedded development: Keil, IAR, PlatformIO, debugging interfaces.",
        "Testing tools: Oscilloscopes, Mixed signal analyzers, Power analyzers."
      ]
    },
    3: {
      title: "Junior Research Fellow ( JRF ) – Electronics Design Engineer",
      duration: "Up to 36 months (may extend)",
      position: "Junior Research Fellow (JRF) – Electronics Design Engineer",
      fellowship: "As per current norms",
      location: "Coimbatore, Tamil Nadu",
      type: "Full-time Research Position",
      roleSummary: "We seek for a Junior Research Fellow ( Electronics Design Engineer ) with strong skills in electronic circuit design, power management, sensor interfacing, and circuit analysis. The role involves building prototypes, conducting simulations, and supporting research activities in advanced electronic system development.",
      responsibilities: [
        "Design analogue, digital, and mixed signal electronic circuits.",
        "Perform circuit analysis: stability, frequency response, noise analysis, signal conditioning, and fault diagnostics.",
        "Develop and test power management circuits ( DCDC converters, PMIC interfaces, lowpower designs ).",
        "Integrate and evaluate sensors (environmental, inertial, biomedical, etc.).",
        "Conduct simulations and modeling using engineering tools.",
        "Create schematics, PCB layouts, testing procedures, and documentation.",
        "Strong fundamentals in electronic circuit theory and analysis.",
        "Practical experience with sensors and embedded electronics.",
        "Hands-on testing, debugging, and measurement experience.",
        "Strong analytical and documentation skills."
      ],
      qualifications: [
        "Postgraduate Degree in a Professional Course such as Electronics, Electrical, ECE, Instrumentation, Embedded Systems, etc.",
        "Minimum 1 to 2 years of research experience in R&D projects.",
      ],
      desiredSkills: [
        "Qualify through one of the following national level exams:",
        "CSIR–UGC NET",
        "GATE",
        "Equivalent exams by central government agencies such as DST, DBT, DAE, DOS, DRDO, MHRD, ICAR, ICMR, IIT, IISc, IISER, etc.",
        "( *- In case candidate has not qualified for the exams above or meet the essential criteria, then they will qualify only as Project associate )",
      ],
      
      engineeringSoftwareTools: [
        "Circuit simulation: LTspice, PSpice, Multisim.",
        "PCB design: Altium Designer, KiCad, OrCAD.",
        "Embedded & debugging tools: Keil, STM32Cube, MPLAB, Logic Analyzers, Oscilloscopes.",
        "Data analysis: MATLAB / Python."
      ]
    },
    4: {
      title: "Research Associate I – ML / AI Engineer - Livestock AI",
      duration: "Up to 36 months (may extend)",
      position: "Research Associate I – ML / AI Engineer",
      fellowship: "As per current norms",
      location: "Coimbatore, Tamil Nadu",
      type: "Full-time Research Position",
      roleSummary: "We seek for a Research Associate I ( ML / AI ), who will lead the machine learning and AI architecture for disease prediction / modelling, forecasting, and real-time treatment monitoring, enabling autonomous and explainable decision-making within the treatment system.",
      responsibilities: [
        "Lead the design and deployment of machine learning models for diseases prediction, progression forecasting, and treatment response assessment datasets.",
        "Develop timeseries and multivariate models integrating biological markers, sensor data, and environmental parameters.",
        "Implement explainable AI pipelines to quantify the influence of animals specific, pathogens specific, and environmental factors on inflammation specific, and environmental factors on inflammation occurrence and recovery.",
        "Design AI decision logic for real-time treatment modulation, enabling adaptive control of electromagnetic stimulation parameters.",
        "Build robust data pipelines for ingestion, cleaning, labelling, and versioning of biological and sensor datasets sourced from partner institutions.",
        "Validate model performance using cross farm, farm, cross species, and longitudinal validation strategies to ensure generalisability.",
        "Interface ML outputs with embedded systems and dashboards, ensuring latency aware and fault tolerant operation.",
        "Support deployment of AI models into edge or near edge environments for on farm farm operation with limited connectivity.",
        "Prepare technical documentation supporting regulatory submissions, publications, and intellectual property generation."
      ],
      qualifications: [
        "PhD ( submitted / awarded ) in Artificial Intelligence / Data Science / Computer Science / Electronics",
        "Minimum 3 to 4 years of research experience in R&D projects",
        "Timeseries forecasting, BIOMEDICAL data analytics",
        "Explainable AI ( SHAP, LIME, feature attribution )",
        "Realtime; Edge AI systems",
        "Prior Healthcare, Veterinary, or Bio Signal ML exposure"
      ]
    },
    5: {
      title: "Senior Research Fellow ( SRF ) - ML/AI Engineer – Livestock AI",
      duration: "Up to 36 months (may extend)",
      position: "Senior Research Fellow (SRF) – ML/AI Engineer",
      fellowship: "As per current norms",
      location: "Coimbatore, Tamil Nadu",
      type: "Full-time Research Position",
      roleSummary: "We seek for a SRF, who will lead the ML / AI workstream: design the end-to-end ML strategy for Diseases prediction, enable real-time treatment monitoring, and collaborate with hardware / control teams to support AI controlled electrical stimulation that regulates signal strength based on infection rate ( as described in the proposal ).",
      responsibilities: [
        "Architect and lead ML development for disease modelling / forecasting & risk scoring, including robust validation strategies.",
        "Implement explainable ML to identify key drivers ( animal / pathogen / environment ) influencing diseases occurrence.",
        "Build and govern data pipelines with partner institutions; ensure dataset documentation and quality controls.",
        "Collaborate with the system team to integrate ML outputs into the AI controlled treatment platform and real-time monitoring tools.",
        "Contribute to project milestones, quarterly reporting expectations, and deliverables tied to software / AI system outcomes."
      ],
      qualifications: [
        "M.E. / M.Tech / PhD ( submitted / ongoing / completed ) in AI / DS / CSE / ECE / EEE / Biomedical or allied discipline AND relevant hands-on on experience in applied ML ( research or product ).",
        "Minimum 2 to 3 years of research experience in R&D projects.",
        "Timeseries modelling, series modelling, weak / label / noisy label learning, imbalanced data approaches.",
        "Explainability tooling ( e.g., SHAP / LIME ) and model governance.",
        "Realtime analytics or edge /embedded ML exposure ( nice to have ).",
        "Prior work with healthcare / biomedical / veterinary datasets ( nice to have )."
      ],
     desiredSkills: [
        "Qualify through one of the following national level exams:",
        "CSIR–UGC NET",
        "GATE",
        "Equivalent exams by central government agencies such as DST, DBT, DAE, DOS, DRDO, MHRD, ICAR, ICMR, IIT, IISc, IISER, etc.",
        "( *- In case candidate has not qualified for the exams above or meet the essential criteria, then they will qualify only as Project associate )",
      ],
    },
    6: {
      title: "Junior Research Fellow ( JRF ) - ML / AI Engineer - Livestock AI",
      duration: "Up to 36 months (may extend)",
      position: "Junior Research Fellow (JRF) – ML / AI Engineer",
      fellowship: "As per current norms",
      location: "Coimbatore, Tamil Nadu",
      type: "Full-time Research Position",
      roleSummary: "We seek for a JRF (ML/AI Engineer), who will support development of machine learning models for diseases risk prediction and treatment monitoring, contribute to data pipelines, and help implement explainable ML for understanding animal/pathogen/environment factors influencing diseases.",
      responsibilities: [
        "Assist in building ML models to predict disease occurrence and progression ( forecasting )",
        "Work on feature engineering across animal, pathogen and environmental factors; implement explainability approaches.",
        "Support integration of ML outputs into the treatment monitoring dashboard / mobile app.",
        "Participate in data acquisition / curation with partners.",
        "Documentation for technical reports and R&D deliverables."
      ],
      qualifications: [
        "B.E. / B.Tech / M.E. / M.Tech in CSE / AI / DS / ECE / EEE / Biomedical / Instrumentation or allied disciplines with strong ML foundations.",
        "Minimum 1 to 2 years of research experience in R&D projects.",
        "ML fundamentals: classification/regression/timeseries, evaluation, model selection.",
        "Python ML stack ( NumPy / pandas / scikitlearn ); exposure to deep learning ( PyTorch / TensorFlow )",
        "Data handling: cleaning, missingness, imbalance handling, validation design.",
        "Basic familiarity with sensors / biomedical signals ( helpful but not mandatory )"
      ],
     desiredSkills: [
        "Qualify through one of the following national level exams:",
        "CSIR–UGC NET",
        "GATE",
        "Equivalent exams by central government agencies such as DST, DBT, DAE, DOS, DRDO, MHRD, ICAR, ICMR, IIT, IISc, IISER, etc.",
        "( *- In case candidate has not qualified for the exams above or meet the essential criteria, then they will qualify only as Project associate )",
      ],
    },
    7: {
      title: "Research Associate I - Microbiologist – Livestock AI",
      duration: "Up to 36 months (may extend)",
      position: "Research Associate I – Microbiologist",
      fellowship: "As per current norms",
      location: "Coimbatore, Tamil Nadu",
      type: "Full-time Research Position",
      roleSummary: "We are seeking a highly motivated Research Associate I (PhD – Microbiology / Biotechnology) to serve as the independent biological lead for a research program developing and validating a novel non-invasive treatment platform for livestock mastitis.\n\nThis role will ensure scientific rigor, biosafety compliance, and clinical trial readiness. The position intersects Veterinary Microbiology, Biomedical Instrumentation, and AI-assisted diagnostics and therapeutic interventions.",
      responsibilities: [
        "Lead end-to-end microbiological characterisation of physiological, biochemical, and microbial markers associated with inflammatory and infectious states in livestock.",
        "Perform antimicrobial susceptibility testing (AST/MIC) and resistance profiling.",
        "Design and execute controlled in vitro and ex vivo experiments to evaluate treatment effects on pathogen viability, growth kinetics, and biofilm behaviour.",
        "Develop diagnostic and intervention workflows capable of generating real-time biological data to improve animal health outcomes.",
        "Ensure experimental reproducibility using appropriate controls, statistical methods, and validation protocols.",
        "Serve as the technical bridge between Life Sciences and Engineering/AI teams, translating biological insights into product development requirements.",
        "Validate AI model outputs using laboratory-verified experimental results.",
        "Develop, implement, and maintain SOPs for all microbiological procedures, ensuring version control and audit compliance.",
        "Define quantitative biological acceptance criteria for treatment efficacy (e.g., pathogen load reduction, SCC normalisation) and safety (absence of cytotoxicity; preservation of milk quality) to support preclinical-to-clinical progression.",
        "Prepare experimental protocols and reports aligned with Good Laboratory Practice (GLP) and institutional research governance standards.",
        "Generate structured microbiological datasets and contribute to clinical documentation and IP filings.",
        "Coordinate laboratory timelines, reagent procurement, and equipment scheduling to ensure milestone adherence."
      ],
      qualifications: [
        "PhD in Microbiology / Biotechnology (or closely related field).",
        "Proficiency in core microbiological techniques: aseptic technique, culture isolation, Gram staining, biochemical identification, disc diffusion, broth microdilution, and time-kill assays.",
        "Strong understanding of antimicrobial resistance mechanisms, MIC testing, and principles of antibiotic stewardship.",
        "Working knowledge of host–pathogen interactions, biofilm biology, and virulence mechanisms in infectious diseases.",
        "Experience in preclinical, clinical, or field-based research projects.",
        "Demonstrated ability to independently design experiments, interpret data, and publish findings (peer-reviewed publications, thesis work, or equivalent evidence)."
      ]
    },
    8: {
      title: "Senior Research Fellow ( SRF ) - Microbiologist – Livestock AI",
      duration: "Up to 36 months (may extend)",
      position: "Senior Research Fellow (SRF) – Microbiologist",
      fellowship: "As per current norms",
      location: "Coimbatore, Tamil Nadu",
      type: "Full-time Research Position",
      roleSummary: "We seek for a Senior Research Fellow ( Microbiologist ), who will be responsible for the biological and microbiological validation of the proposed treatment system. The role is critical to establishing the scientific mechanism of action, supporting wet lab experimentation, and contributing to clinical trial readiness.",
      responsibilities: [
        "Lead microbiological investigations related to diseases causing pathogens.",
        "Design and oversee wet lab experiments using milk and samples from infected livestock.",
        "Evaluate pathogen response to treatment.",
        "Support validation using sample preparation and other biological indicators.",
        "Ensure compliance with biosafety, institutional ethics, and animal research protocols.",
        "Contribute to clinical trial design, biological data interpretation, and reporting.",
        "Collaborate with electronics and ML / AI teams to correlate biological outcomes with system outputs.",
        "Assist in preparation of technical reports, publications, and IP documentation."
      ],
      qualifications: [
        "M.Sc. / M. Tech PhD ( submitted / ongoing / completed ) in Microbiology or closely related life science discipline.",
        "Minimum 2 to 3 years of research experience in R&D projects.",
        "Experience working with bacterial pathogens related to veterinary or dairy science.",
        "Familiarity with veterinary diagnostics, milk microbiology, or animal health research.",
        "Exposure to clinical or preclinical studies involving livestock.",
        "Ability to work in interdisciplinary R&D teams ( biology + engineering + AI )"
      ],
      desiredSkills: [
        "Qualify through one of the following national level exams:",
        "CSIR–UGC NET",
        "GATE",
        "Equivalent exams by central government agencies such as DST, DBT, DAE, DOS, DRDO, MHRD, ICAR, ICMR, IIT, IISc, IISER, etc.",
        "( *- In case candidate has not qualified for the exams above or meet the essential criteria, then they will qualify only as Project associate )",
      ],
    },
    9: {
      title: "Junior Research Fellow - Biotechnologist - Livestock AI",
      duration: "Up to 36 months (may extend)",
      position: "Junior Research Fellow (JRF) – Biotechnologist",
      fellowship: "As per current norms",
      location: "Coimbatore, Tamil Nadu",
      type: "Full-time Research Position",
      isClosed: false,
      roleSummary: "We seek for a Junior Research Fellow ( Biotechnologist ), who will support microbiological and biological experiments, assist in wet lab activities, and contribute to data generation and biological validation of the proposed treatment system. The role works closely with the SRF (Microbiologist) and the electronics and ML/AI teams.",
      responsibilities: [
        "Assist in sample collection, preparation, and processing ( milk, contaminated swabs ) from livestock.",
        "Support wet lab experiments related to inflammatory pathogens under supervision.",
        "Assist in biological evaluation of treatment outcomes.",
        "Maintain laboratory records, protocols, and documentation.",
        "Support creation and annotation of biological datasets for ML / AI model development.",
        "Ensure adherence to biosafety, ethical, and animal research guidelines.",
        "Assist in preparation of technical reports and project deliverables."
      ],
      qualifications: [
        "M.Sc. in Biotechnology / Life Sciences / Microbiology / Biochemistry or equivalent discipline from a recognized university.",
        "Minimum 1 to 2 years of research experience in R&D projects.",
        "Basic experience in wet lab techniques ( microbiology / molecular biology / biochemistry )",
        "Familiarity with animal health, dairy science, or veterinary research ( desirable )",
        "Ability to work in an interdisciplinary research environment.",
        "Willingness to work with field samples and livestock research settings."
      ],
     desiredSkills: [
        "Qualify through one of the following national level exams:",
        "CSIR–UGC NET",
        "GATE",
        "Equivalent exams by central government agencies such as DST, DBT, DAE, DOS, DRDO, MHRD, ICAR, ICMR, IIT, IISc, IISER, etc.",
        "( *- In case candidate has not qualified for the exams above or meet the essential criteria, then they will qualify only as Project associate )",
      ],
    },
    10: {
      title: "Project Assistant – Microbiology / Biotechnology",
      duration: "Up to 36 months (may extend)",
      position: "Project Assistant – Microbiology / Biotechnology",
      fellowship: "As per current norms",
      location: "Coimbatore, Tamil Nadu",
      type: "Full-time Research Position",
      isClosed: false,
      roleSummary: "We seek for a Project Assistant ( Microbiology / Biotechnology ), who will support wetlab execution, routine microbiological workflows, biological data generation, sample processing, and documentation related to livestock research. The role focuses on throughput, accuracy, and compliance, enabling senior researchers to concentrate on experimental design and data interpretation.",
      responsibilities: [
        "Assist in preparation of culture media, reagents, and laboratory consumables.",
        "Support collection, handling, and processing of milk and udder samples.",
        "Perform routine microbial culturing, subculturing, and plate maintenance.",
        "Assist with CFU plating, colony counting, and basic viability assays.",
        "Assist in sample preprocessing and preparation for biological analysis.",
        "Maintain sample metadata, biological datasets, and experiment records.",
        "Support microbiological experiments under supervision of SRF / RA I.",
        "Assist in basic assays and data capture under supervision.",
        "Support formatting and structuring of data for ML / AI model ingestion.",
        "Maintain laboratory logbooks, sample registers, and biosafety records.",
        "Ensure adherence to biosafety, sterilisation, and wastedisposal protocols.",
        "Ensure completeness of experimental documentation for reports and audits.",
        "Assist in preparation of periodic progress reports and datasets.",
        "Coordinate with microbiology and AI teams for data consistency.",
        "Support preparation of data records for reporting and audits."
      ],
      qualifications: [
        "B.Sc. / M.Sc. in Microbiology / Biotechnology / Life Sciences / Biochemistry or equivalent discipline from a recognised university.",
        "Hands-on exposure to basic microbiology laboratory techniques.",
        "Understanding of basic biological assays and data recording.",
        "Familiarity with sterile lab practices and biosafety norms.",
        "Familiarity with spreadsheets and structured data entry.",
        "Ability to handle biological samples methodically.",
        "Attention to detail and documentation discipline.",
        "Ability to work in interdisciplinary research teams.",
        "Willingness to work in experimental and fieldlinked research environments."
      ]
    },
    11: {
      title: "Project Assistant – Electronics Design Engineer",
      duration: "Up to 36 months (may extend)",
      position: "Project Assistant – Electronics Design Engineer",
      fellowship: "As per current norms",
      location: "Coimbatore, Tamil Nadu",
      type: "Full-time Research Position",
      isClosed: false,
      roleSummary: "We seek a proactive Project Assistant – Electronics Design to support the development of electronic systems from concept to prototype. This role is ideal for candidates passionate about hardware design, embedded electronics, prototyping, testing, and documentation, and who want hands-on exposure to real-world engineering projects. You will work closely with senior engineers and multi-disciplinary teams to assist in schematic design, PCB development, component selection, debugging, and validation for product and R&D initiatives.",
      responsibilities: [
        "Assist in electronics circuit design including analog, digital, and mixed-signal subsystems",
        "Support creation and review of schematics, BOMs, and PCB layouts using EDA tools",
        "Assist with component selection, availability checks, and alternative part identification",
        "Support prototype assembly, bring-up, and testing (bench testing, validation, debugging)",
        "Use lab instruments such as oscilloscope, multimeter, function generator, power supply, logic analyzer",
        "Help with test planning, test reports, and documenting design iterations",
        "Support integration with embedded firmware teams (interfaces like I2C, SPI, UART, ADC/DAC, PWM)",
        "Assist with EMI/EMC-aware design practices and basic signal/power integrity checks",
        "Maintain project files, version control, and documentation aligned to internal standards",
        "Collaborate with mechanical/software teams for enclosure, connectors, thermal, and system integration needs"
      ],
      qualifications: [
        "Bachelor's degree (or final-year student) in Electronics & Communication, Electrical, Embedded Systems, Instrumentation, Mechatronics, or related fields",
        "Strong fundamentals in: Analog & digital electronics",
        "Power supplies/regulators (basic DC-DC, LDO concepts)",
        "Circuit analysis and troubleshooting",
        "Familiarity with at least one EDA tool (any one is sufficient): KiCad / Altium / Eagle / OrCAD / Proteus",
        "Basic understanding of embedded interfaces and components: Sensors, microcontrollers, op-amps, comparators, ADCs, voltage references",
        "Comfortable reading datasheets, application notes, and reference designs",
        "Good documentation habits and a structured approach to engineering tasks"
      ]
    },
    12: {
      title: "Project Assistant – ML/AI Engineer - Livestock AI",
      duration: "Up to 36 months (may extend)",
      position: "Project Assistant – ML/AI Engineer",
      fellowship: "As per current norms",
      location: "Coimbatore, Tamil Nadu",
      type: "Full-time Research Position",
      isClosed: false,
      roleSummary: "We seek a motivated Project Assistant – Machine Learning & AI to support the design, development, and deployment of intelligent systems across applied research and industry-focused projects. The role is ideal for candidates passionate about data-driven problem solving, AI model development, and real-world impact, and who are eager to learn by working closely with senior engineers, researchers, and domain experts. As a Project Assistant, you will contribute to end-to-end AI/ML project execution, from data preparation and experimentation to model evaluation and documentation.",
      responsibilities: [
        "Assist in the development and testing of machine learning and AI models for real-world applications",
        "Support data collection, cleaning, labeling, and exploratory data analysis (EDA)",
        "Implement and experiment with ML algorithms including regression, classification, clustering, and deep learning models",
        "Help train, fine-tune, and evaluate models using appropriate performance metrics",
        "Support integration of AI models into software systems, dashboards, or prototypes",
        "Assist in preparing technical documentation, reports, and presentations for internal and external stakeholders",
        "Collaborate with cross-functional teams including software, hardware, domain, and research teams",
        "Stay updated with emerging trends, tools, and best practices in AI, ML, and data science"
      ],
      qualifications: [
        "Bachelor's degree (or final-year student) in Computer Science, AI & Data Science, Information Technology, Electronics, or related fields",
        "Basic understanding of Machine Learning concepts and AI fundamentals",
        "Hands-on experience with Python and common ML libraries such as: NumPy, Pandas, Scikit-learn",
        "TensorFlow / PyTorch (basic familiarity)",
        "Understanding of data structures, algorithms, and statistics",
        "Ability to work with datasets and interpret results logically",
        "Strong willingness to learn, experiment, and work in a project-driven environment"
      ]
    }
  };

  const job = jobDetails[jobId];

  if (!job) {
    return (
      <Layout>
        <Helmet>
          <title>Position Not Found - Adept Knowledge Technologies</title>
          <meta name="description" content="This job position could not be found. Explore all open positions at Adept Knowledge Technologies." />
          <link rel="canonical" href="https://www.ak-tech.in/careers" />
        </Helmet>
        <div className="job-not-found">
          <h2>Job not found</h2>
          <Link to="/careers">Back to Career Page</Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>{job.title} - Adept Knowledge Technologies</title>
        <meta name="description" content={`${job.roleSummary ? job.roleSummary.slice(0, 155).replace(/\n/g, ' ') + '…' : `Apply for ${job.title} at Adept Knowledge Technologies. Based in Coimbatore, Tamil Nadu.`}`} />
        <link rel="canonical" href={`https://www.ak-tech.in/careers/${jobSlug}`} />
        <meta property="og:title" content={`${job.title} - Adept Knowledge Technologies`} />
        <meta property="og:description" content={`Apply for ${job.title} at Adept Knowledge Technologies. ${job.location} — ${job.type}.`} />
        <meta property="og:url" content={`https://www.ak-tech.in/careers/${jobSlug}`} />
        <meta name="twitter:title" content={`${job.title} - Adept Knowledge Technologies`} />
        <meta name="twitter:description" content={`Apply for ${job.title} at Adept Knowledge Technologies. ${job.location} — ${job.type}.`} />
      </Helmet>
      <section className="job-detail-section">
        <div className="job-detail-container">
          {/* Breadcrumbs */}
          <nav className="breadcrumbs" data-aos="fade-up">
            <Link to="/">Home</Link>
            <span className="separator">/</span>
            <Link to="/careers">Careers</Link>
            <span className="separator">/</span>
            <span className="current">{job.title}</span>
          </nav>

          <div className="job-detail-content" data-aos="fade-up">
            {/* Job Title Outside White Section */}
            <div className="job-title-section">
              <h2>{job.location}</h2>
              <h1>{job.title}</h1>
              <button 
                className={`header-apply-btn ${job.isClosed ? 'disabled' : ''}`}
                onClick={() => !job.isClosed && navigate(`/careers/${jobSlug}/apply`)}
                disabled={job.isClosed}
              >
                {job.isClosed ? 'Position Closed' : 'Apply now →'}
              </button>
            </div>

            {/* White Description Panel */}
            <div className="job-description-panel">
              <div className="job-detail-info">
                <div className="job-description-column">
                  <h3>Job Description</h3>
                  {job.roleSummary.split('\n\n').map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}

                  <h4>Key Responsibilities:</h4>
                  <ul>
                    {job.responsibilities.map((responsibility, index) => (
                      <li key={index}>{responsibility}</li>
                    ))}
                  </ul>

                  <h4>Essential Qualifications:</h4>
                  <ul>
                    {job.qualifications.map((qualification, index) => (
                      <li key={index}>{qualification}</li>
                    ))}
                  </ul>

                  {job.desiredSkills && (
                    <>
                      <h4>Desired Skills (Not Mandatory)*</h4>
                      <p>{job.desiredSkills[0]}</p>
                      <ul>
                        {job.desiredSkills.slice(1, -1).map((skill, index) => (
                          <li key={index}>{skill}</li>
                        ))}
                      </ul>
                      <p style={{ marginTop: '10px' }}>{job.desiredSkills[job.desiredSkills.length - 1]}</p>
                    </>
                  )}

                  {job.eligibilityCriteria && (
                    <>
                      <h4>Eligibility Criteria:</h4>
                      <ul>
                        {job.eligibilityCriteria.map((criteria, index) => (
                          <li key={index}>{criteria}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {job.engineeringSoftwareTools && (
                    <>
                      <h4>Engineering Software and Tools:</h4>
                      <ul>
                        {job.engineeringSoftwareTools.map((tool, index) => (
                          <li key={index}>{tool}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>

                <div className="job-info-card">
                  
                  <div className="info-item">
                    <span className="info-label">Contract type:</span>
                    <span className="info-value">{job.type}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Location:</span>
                    <span className="info-value">{job.location}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default JobDetailPage;